import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Share2, RefreshCcw, ArrowRight, Check, Sparkles, ImageDown, Heart } from "lucide-react";
import { downloadPersonaCard } from "../../lib/personaShareCard";
import { PersonaPlan } from "./PersonaPlan";
import { PersonaChart } from "./result/PersonaChart";
import { AdvanceRows } from "./result/AdvanceRows";
import { AdvanceHabits } from "./result/AdvanceHabits";
import { ProductPreview } from "./result/ProductPreview";
import { ResultCta } from "./result/ResultCta";

/**
 * Reveal screen for the MoneyMind™ Quiz result (owner + AI CMO spec, 2026-10-31).
 *
 * Order: persona identity → signature quote → reframe → MoneyMind Persona
 * Chart → How Your Persona Can Advance → personalized habits → product
 * preview → CTA. Then Strengths / Growth Edges, the 4-week plan, couples
 * invite, share/retake and the bottom CTA. Three tracked signup placements:
 * top, preview, bottom. No trait-model terminology anywhere visitor-facing.
 */
export default function QuizResult({ result, onRetake }) {
  const {
    name, icon, tagline, color, slug,
    strengths, growth_edges: growthEdges,
    auto_enabled_habit: autoEnabledHabit,
    pain, advancement, is_member: isMember,
    lead_email: leadEmail,
    contest_guess: contestGuess,
    contest_guess_name: contestGuessName,
    contest_guess_correct: contestGuessCorrect,
    contest_entered: contestEntered,
  } = result;
  const shortName = name.replace(/^The /, "");
  const cta = { slug, leadEmail, isMember };

  return (
    <div className="pb-32" data-testid="quiz-result">
      <header className="border-b border-[#E6E3DB]">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#2C302E]">
            Finhabits
          </Link>
          {!isMember && (
            <Link
              to="/login"
              className="text-xs uppercase tracking-wider text-[#787671] hover:text-[#2C302E]"
            >
              Sign in to save your result
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 mt-12">
        <PersonaCard icon={icon} name={name} tagline={tagline} color={color} />
        {contestGuess && (
          <GuessReveal
            guessName={contestGuessName}
            actualName={name}
            correct={contestGuessCorrect}
            entered={contestEntered}
            color={color}
          />
        )}
        {pain && <ReliefBlock pain={pain} color={color} />}
        {autoEnabledHabit && <StarterHabitBanner habit={autoEnabledHabit} color={color} />}

        {!(isMember && autoEnabledHabit) && (
          <ResultCta {...cta} placement="top" compact lede={`Put the ${shortName} pattern on a calendar.`} />
        )}

        <PersonaChart chart={advancement?.chart} color={color} />
        <AdvanceRows advance={advancement?.advance} />
        <AdvanceHabits habits={advancement?.habits} name={name} />
        <ProductPreview benefits={advancement?.preview_benefits} />

        <ResultCta
          {...cta}
          placement="preview"
          bridge
          shortName={shortName}
          lede={
            <>
              Your result shows <strong>how you tend to handle money</strong>. The next 60 days
              are where the {shortName} habits above become routine — on your own Spending Plan.
            </>
          }
        />

        <div className="mt-16 pt-12 border-t border-[#E6E3DB]" data-testid="result-full-breakdown">
          <p className="eyebrow text-[#787671] text-center">Your full breakdown</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ListCard title="Strengths" items={strengths} accent="#3E6B37" testid="result-strengths" />
            <ListCard title="Growth edges" items={growthEdges} accent="#C05C3D" testid="result-growth-edges" />
          </div>
        </div>

        <PersonaPlan slug={slug} name={name} color={color} isMember={isMember} />
        <PartnerInvite name={name} icon={icon} />
        <ShareRow name={name} icon={icon} tagline={tagline} color={color} onRetake={onRetake} />

        <ResultCta {...cta} placement="bottom" lede="Ready when you are." />

        <p className="mt-10 text-xs text-[#9b9892] leading-relaxed text-center max-w-2xl mx-auto">
          Persona tendencies are drawn from your quiz answers, not from your accounts or credit
          report. For informational purposes only. Not financial advice. Consult a qualified
          financial advisor before making any financial decisions.
        </p>
      </main>
    </div>
  );
}


/* Contest reveal — "you guessed X, you're actually Y" moment. */
function GuessReveal({ guessName, actualName, correct, entered, color }) {
  return (
    <section
      className={`mt-6 rounded-sm border-l-4 p-5 sm:p-6 ${
        correct ? "bg-[#F0F5EE]" : "bg-[#FBF6EC]"
      }`}
      style={{ borderColor: correct ? "#607762" : color }}
      data-testid="result-guess-reveal"
    >
      {correct ? (
        <>
          <p className="font-serif text-xl text-[#2C302E]">
            🎯 You called it — you guessed <strong>{guessName}</strong> and
            that&apos;s exactly who you are.
          </p>
          {entered ? (
            <p className="mt-2 text-sm text-[#4d4f4c]" data-testid="result-contest-entered">
              You&apos;re entered in this month&apos;s drawing for a{" "}
              <strong>free year of membership</strong> ($120 value). Winners are
              notified by email.{" "}
              <Link to="/contest-rules" className="underline">Contest rules</Link>
            </p>
          ) : (
            <p className="mt-2 text-sm text-[#4d4f4c]" data-testid="result-contest-already">
              You&apos;re already entered this month — your first guess is the one
              that counts.{" "}
              <Link to="/contest-rules" className="underline">Contest rules</Link>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="font-serif text-xl text-[#2C302E]">
            You guessed <strong>{guessName}</strong> — but you&apos;re actually{" "}
            <strong>{actualName}</strong>.
          </p>
          <p className="mt-2 text-sm text-[#4d4f4c]">
            The way we see ourselves and the way we actually behave with money
            don&apos;t always match. That gap is exactly what your result below
            explains.
          </p>
        </>
      )}
    </section>
  );
}


function PersonaCard({ icon, name, tagline, color }) {
  return (
    <section
      className="p-8 sm:p-12 text-white relative overflow-hidden"
      style={{ background: color }}
      data-testid="result-persona-card"
    >
      <p className="eyebrow text-white/70">Your MoneyMind&trade;</p>
      <div className="flex items-center gap-4 mt-3">
        <span className="text-5xl sm:text-6xl" aria-hidden>{icon}</span>
        <h1
          className="font-serif text-4xl sm:text-5xl leading-tight"
          data-testid="result-persona-name"
        >
          {name}
        </h1>
      </div>
      <p className="mt-5 font-serif text-xl italic text-white/90 max-w-2xl" data-testid="result-persona-tagline">
        "{tagline}"
      </p>
    </section>
  );
}


/* The "ah, that's me" moment without shame — pattern story + reframe. */
function ReliefBlock({ pain, color }) {
  return (
    <section className="mt-8" data-testid="result-relief">
      <p className="eyebrow text-[#C05C3D]">Sound familiar?</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#2C302E] mt-2 leading-tight">
        You&apos;re not broken — you&apos;re patterned.
      </h2>
      <p
        className="mt-4 font-serif text-xl italic text-[#4d4f4c] leading-relaxed max-w-2xl border-l-4 pl-5"
        style={{ borderColor: color }}
        data-testid="result-pattern-story"
      >
        {pain.pattern}
      </p>
      <p className="mt-4 text-[#4d4f4c] leading-relaxed max-w-2xl" data-testid="result-reframe">
        {pain.relief_reframe}
      </p>
    </section>
  );
}


/* Partner invite — every completed quiz can become two leads. */
function PartnerInvite({ name, icon }) {
  const [copied, setCopied] = useState(false);

  const invite = async () => {
    const url = `${window.location.origin}/quiz?ref=couple-invite`;
    const text = `I just found out my MoneyMind\u2122 is ${icon} ${name}. Take the free 5-minute quiz so we can see how our two MoneyMinds work together:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "MoneyMind Quiz", text, url });
        return;
      } catch {
        // user dismissed the share sheet — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt("Copy this to send:", `${text} ${url}`);
    }
  };

  return (
    <section
      className="mt-12 bg-[#FBFAF6] border border-[#E6E3DB] px-6 py-8 md:px-10 text-center"
      data-testid="result-partner-invite"
    >
      <Heart className="h-6 w-6 mx-auto text-[#C05C3D]" />
      <p className="eyebrow text-[#C05C3D] mt-3">For couples</p>
      <h2 className="font-serif text-3xl mt-1">Does your partner know their MoneyMind?</h2>
      <p className="mt-3 text-sm text-[#4d4f4c] max-w-lg mx-auto leading-relaxed">
        Invite them to take the free quiz — then see how your two MoneyMinds work
        together: your combined strengths, your one watch-for, and the first habit
        loop built for the pair of you.
      </p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={invite}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C302E] text-white rounded-sm hover:bg-[#1a1d1c] transition-colors"
          data-testid="result-partner-invite-button"
        >
          {copied ? <Check className="h-4 w-4" /> : <Heart className="h-4 w-4" />}
          {copied ? "Link copied — send it!" : "Invite your partner"}
        </button>
        <Link
          to="/couples"
          className="text-sm text-[#2F6F8F] underline hover:text-[#2C302E]"
          data-testid="result-partner-invite-couples-link"
        >
          See how pairings work →
        </Link>
      </div>
    </section>
  );
}


/* Share / download card / retake. */
function ShareRow({ name, icon, tagline, color, onRetake }) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/quiz?ref=persona-share`;
    const text = `My MoneyMind\u2122 persona is ${icon} ${name} — "${tagline}". What's yours?`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "MoneyMind Quiz", text, url });
        return;
      } catch {
        // user dismissed the share sheet — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} Take the free quiz: ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt("Copy this to share:", `${text} ${url}`);
    }
  };

  return (
    <div className="mt-12 flex flex-wrap items-center gap-3 justify-center" data-testid="result-share-row">
      <button
        onClick={handleShare}
        className="inline-flex items-center gap-2 px-5 py-3 bg-[#2C302E] text-white rounded-sm hover:bg-[#1a1d1c] transition-colors"
        data-testid="result-share-button"
      >
        {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
        {copied ? "Copied!" : "Share my MoneyMind"}
      </button>
      <button
        onClick={() => downloadPersonaCard({ name, icon, tagline, color })}
        className="inline-flex items-center gap-2 px-5 py-3 border border-[#2C302E] text-[#2C302E] rounded-sm hover:bg-[#2C302E] hover:text-white transition-colors"
        data-testid="result-download-card-button"
      >
        <ImageDown className="h-4 w-4" />
        Download share card
      </button>
      <button
        onClick={onRetake}
        className="inline-flex items-center gap-2 px-5 py-3 border border-[#2C302E] text-[#2C302E] rounded-sm hover:bg-[#2C302E] hover:text-white transition-colors"
        data-testid="result-retake-button"
      >
        <RefreshCcw className="h-4 w-4" />
        Take it again
      </button>
    </div>
  );
}


function ListCard({ title, items, accent, testid }) {
  return (
    <div className="p-6 border" style={{ borderColor: accent }} data-testid={testid}>
      <p className="eyebrow" style={{ color: accent }}>
        {title}
      </p>
      <ul className="mt-3 space-y-2">
        {items.map((item, i) => (
          <li key={`${title}-${i}-${item.slice(0, 24)}`} className="text-[#2C302E] text-sm leading-relaxed">
            · {item}
          </li>
        ))}
      </ul>
    </div>
  );
}


/**
 * Celebratory banner shown when the backend auto-enabled a starter habit
 * for the authed user immediately after their quiz submission.
 */
function StarterHabitBanner({ habit, color }) {
  return (
    <section
      className="mt-6 p-5 sm:p-6 border-2 border-dashed bg-[#FBFAF6] flex flex-col sm:flex-row sm:items-start gap-4"
      style={{ borderColor: color }}
      data-testid="result-starter-habit-banner"
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: color, color: "#fff" }}
        >
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow" style={{ color }}>
            Already on the dashboard
          </p>
          <p
            className="font-serif text-lg sm:text-xl text-[#2C302E] mt-1 leading-tight"
            data-testid="result-starter-habit-title"
          >
            {habit.is_existing ? "Your active habit: " : "Your starter habit is ready: "}
            <span style={{ color }}>{habit.title}</span>
          </p>
          <p className="text-sm text-[#4d4f4c] mt-1.5">
            {habit.is_existing
              ? "You already have this one switched on — we left it exactly as it is. Open the loop to add a note or mark it done."
              : "We've turned on the one habit your persona tends to compound the fastest from. Open the loop to add a note or mark it done."}
          </p>
        </div>
      </div>
      <Link
        to={`/app/habits#habit-${habit.slug}`}
        className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-white text-sm whitespace-nowrap transition-opacity hover:opacity-90 self-start sm:self-center"
        style={{ background: color }}
        data-testid="result-starter-habit-cta"
      >
        Open habit loop
        <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}
